import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Search as SearchIcon,
  X as XIcon,
  Eye as EyeIcon,
  Check as CheckIcon,
  Loader2 as LoaderIcon,
  AlertTriangle as AlertTriangleIcon,
} from "lucide-react";

const StartupVisibilityModal = ({ isOpen, onClose, onComplete }) => {
  const [startups, setStartups] = useState([]);
  const [selectedStartups, setSelectedStartups] = useState([]);
  const [originalSelectedStartups, setOriginalSelectedStartups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      
      const [startupsResponse, candidateResponse] = await Promise.all([
        axios.get("/api/startups/fetchStartups", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("/api/candidates/fetchCandidate", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      const fetchedStartups = startupsResponse.data.startups || [];
      setStartups(fetchedStartups);

      const candidateData = candidateResponse.data.candidate;
      const hideFromStartups = candidateData?.fields?.["Hide From"] || [];
      setSelectedStartups(hideFromStartups);
      setOriginalSelectedStartups(hideFromStartups);
    } catch (err) {
      console.error("Error fetching data:", err);
      if (err.config.url.includes('fetchStartups')) {
        setError(`Failed to load startups: ${err.response?.data?.message || err.message}`);
      } else {
        console.error("Error fetching candidate data:", err);
        // Non-critical error, continue without candidate's hidden startups
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    } else {
      // Reset state when modal is closed
      setSelectedStartups([]);
      setOriginalSelectedStartups([]);
      setSearchTerm("");
      setError("");
    }
  }, [isOpen]);

  const handleStartupToggle = (startupId) => {
    setSelectedStartups((prev) =>
      prev.includes(startupId)
        ? prev.filter((id) => id !== startupId)
        : [...prev, startupId]
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.post("/api/candidates/updateHideFromStartups", {
        startupIds: selectedStartups,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onComplete();
    } catch (err) {
      console.error("Error updating startup preferences:", err);
      setError("Failed to save preferences. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredStartups = startups.filter((startup) =>
    startup.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const showModal = isOpen;

  return (
    <AnimatePresence>
      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white w-full max-w-2xl max-h-[90vh] rounded-2xl shadow-xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-start justify-between p-6 border-b border-gray-100">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#02B491]/10 flex items-center justify-center">
                  <EyeIcon className="w-6 h-6 text-[#02B491]" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Control Your Visibility
                  </h2>
                  <p className="text-sm text-gray-600 mt-1 max-w-md">
                    Select any companies that you don't want to see your profile. This is useful for hiding your profile from current or past employers.
                  </p>
                </div>
              </div>
            </div>

            {/* Search & Selected */}
            <div className="p-6 border-b border-gray-100">
              <div className="relative">
                <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#02B491] focus:border-transparent transition-all"
                  placeholder="Search for a company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-3">
                  Companies you are hiding your profile from ({selectedStartups.length})
                </h3>
                {selectedStartups.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedStartups.map((startupId) => {
                      const startup = startups.find((s) => s.id === startupId);
                      if (!startup) return null;
                      return (
                        <div
                          key={startupId}
                          className="bg-gray-100 rounded-full px-3 py-1 flex items-center gap-2 text-sm text-gray-800"
                        >
                          <span>{startup.name}</span>
                          <button
                            onClick={() => handleStartupToggle(startupId)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <XIcon size={14} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">
                 You are currrenly not hiding your profile from any companies.
                  </p>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <LoaderIcon className="w-8 h-8 text-gray-400 animate-spin" />
                  <p className="mt-3 text-gray-600">Loading startups...</p>
                </div>
              ) : error && !loading ? (
                <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                  <AlertTriangleIcon className="w-8 h-8 text-red-500" />
                  <p className="mt-3 text-red-600">{error}</p>
                  <button
                    onClick={fetchData}
                    className="mt-4 px-4 py-2 bg-[#02B491] text-white rounded-lg hover:bg-[#029078] transition-colors"
                  >
                    Try Again
                  </button>
                </div>
              ) : searchTerm.length > 0 ? (
                <div className="space-y-1 p-4">
                  {filteredStartups.length > 0 ? (
                    filteredStartups.map((startup) => {
                      const isSelected = selectedStartups.includes(startup.id);
                      return (
                        <label
                          key={startup.id}
                          onClick={() => handleStartupToggle(startup.id)}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                            isSelected
                              ? "bg-[#02B491]/10 border-2 border-[#02B491]/50"
                              : "hover:bg-gray-50 border-2 border-transparent"
                          }`}
                        >
                          <span className="font-medium text-gray-800">
                            {startup.name}
                          </span>
                          <div
                            className={`w-5 h-5 flex items-center justify-center rounded-md border-2 transition-all ${
                              isSelected
                                ? "bg-[#02B491] border-[#02B491]"
                                : "border-gray-300"
                            }`}
                          >
                            {isSelected && (
                              <CheckIcon className="w-4 h-4 text-white" />
                            )}
                          </div>
                        </label>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">
                        We can't find a startup with that name. They may not be
                        on our platform.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 px-6">
                  <p className="text-gray-500">
                    Enter a company name in the search bar above to find and
                    hide your profile from them.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
               <p className="text-sm text-gray-500">Your visibility settings will be saved.</p>
               <div className="flex items-center gap-3">

                 <button
                   onClick={onComplete}
                   className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                   disabled={submitting}
                 >
                   Skip and Proceed
                 </button>
                 <button
                   onClick={handleSubmit}
                   disabled={submitting || loading || JSON.stringify(selectedStartups.sort()) === JSON.stringify(originalSelectedStartups.sort())}
                   className="px-4 py-2 bg-[#02B491] text-white rounded-lg font-medium hover:bg-[#029078] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                 >
                   {submitting ? (
                     <>
                       <LoaderIcon className="w-4 h-4 animate-spin" />
                       Saving...
                     </>
                   ) : (
                     "Save Changes"
                   )}
                 </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default StartupVisibilityModal; 